import { Paper, styled, Typography } from '@mui/material';
import React from 'react';
import defaultIcon from '../../../../assets/qr/qr-default-icon.png';
import { BaseTypography } from '../../../../base/components/BaseTypography';
import { ENV_WARNING_BAR_HEIGHT } from "../../../../base/components/EnviromentWarningBar/EnviromentWarningBar";
import { Flex } from '../../../../base/components/Flex';
import { CompanyQrScenesQuery } from '../../../../graphql/graphql-operations';
import { useTranslationPrefix } from '../../../../hooks/useTranslationPrefix';
import QrInfoTag from "./QrInfoTag";


type Scene = CompanyQrScenesQuery["companyQrScenes"][0]["scene"];

interface Props {
  id?: string;
  scene: Scene;
  onClick?: () => void;
  selected?: boolean;
  size: "small" | "medium" | "large";
  columns: number;
  rows: number;
}

export const QrSceneCardDefaultColor = "#0079C1";

export const QrSceneColors = [
  "#FF6666",
  QrSceneCardDefaultColor,
  "#FFB300",
  "#EE2523",
  "#BF5C00",
  "#06518D",
  "#E00016",
];

const splitByFirstDash = (str: string): [string, string] => {
  const index = str.indexOf("-");
  if (index === -1) return ["\u00A0", str];
  return [str.substring(0, index), str.substring(index + 1)];
};

const QrSceneCard = ({
  id,
  scene,
  onClick,
  selected,
  size,
  columns,
  rows,
  ...restCardProps
}: Props) => {
  const { _t } = useTranslationPrefix("LaunchPlaylistQrPageNew");
  const color = scene.qrCardColor || QrSceneCardDefaultColor;

  const largeVariantTitle = splitByFirstDash(scene.name_t);

  return size === "medium" || size === "large" ? (
    <Paper
      id={id}
      onClick={onClick}
      {...restCardProps}
      elevation={selected ? -2 : 0}
      sx={{
        backgroundColor: scene.qrCardColor || QrSceneCardDefaultColor,
        color: "white.main",
        position: "relative",
        opacity: selected ? 1 : 0.6,
        aspectRatio: "1",
        gridColumn: "span 2",
        width: "100dvw",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        maxHeight: `min(calc((100dvh - 16rem - ${ENV_WARNING_BAR_HEIGHT}) / ${rows}), 30rem)`,
        maxWidth: `min(calc((100dvw - 8rem - ${ENV_WARNING_BAR_HEIGHT}) / ${columns}), 30rem)`,
      }}
    >
      <Flex
        p={size === "large" ? 4 : 2}
        pb={2}
        flexDirection="column"
        justifyContent="start"
        alignItems="center"
        gap={2}
      >
        <Flex flexDirection="column" alignItems="center">
          <BaseTypography
            variant={size === "large" ? "h4" : "h6"}
            color="inherit"
            textAlign="center"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              opacity: "0.85",
            }}
          >
            {largeVariantTitle[0]}
          </BaseTypography>
          <BaseTypography
            height={size === "large" ? "6rem" : "4rem"}
            variant={size === "large" ? "h2" : "h4"}
            color="inherit"
            textAlign="center"
            sx={{
              wordBreak: "break-word",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              "-webkit-line-clamp": "2",
              lineClamp: "2",
              "-webkit-box-orient": "vertical",
              boxOrient: "vertical",
            }}
          >
            {largeVariantTitle[1]}
          </BaseTypography>
        </Flex>
        <QrIcon
          src={scene.qrIconUrl || defaultIcon}
          alt={scene.name_t}
          sx={{
            width: size === "large" ? "10rem" : "5rem",
          }}
        />
        <Flex width="100%" mt="auto">
          <Typography
            fontSize="1.5rem"
            sx={{
              position: "absolute",
              left: 16,
              bottom: 16,
            }}
          >
            {scene.qrLengthMinutes
              ? _t("durationMinutes", "{{count}} min", {
                  count: scene.qrLengthMinutes,
                })
              : "\u00A0"}
          </Typography>
          {scene.description &&
            scene.description !== "Missing Translation!" && (
              <QrInfoTag
                color={color}
                description={scene.description}
                size="large"
              />
            )}
        </Flex>
      </Flex>
    </Paper>
  ) : (
    <Paper
      id={id}
      onClick={onClick}
      {...restCardProps}
      elevation={selected ? -2 : 0}
      sx={{
        backgroundColor: scene.qrCardColor || QrSceneCardDefaultColor,
        color: "white.main",
        position: "relative",
        opacity: selected ? 1 : 0.6,
        gridColumn: "span 2",
      }}
    >
      <Flex p={1} justifyContent="start" alignItems="center" gap={2}>
        <QrIcon src={scene.qrIconUrl || defaultIcon} alt={scene.name_t} />
        <Flex flexDirection="column">
          <BaseTypography
            variant="h5"
            color="inherit"
            sx={{
              wordBreak: "break-word",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              "-webkit-line-clamp": "2",
              lineClamp: "2",
              "-webkit-box-orient": "vertical",
              boxOrient: "vertical",
              minHeight: "3.5rem",
            }}
          >
            {scene.name_t}
          </BaseTypography>
          <Flex>
            <Typography>
              {scene.qrLengthMinutes
                ? _t("durationMinutes", "{{count}} min", {
                    count: scene.qrLengthMinutes,
                  })
                : "\u00A0"}
            </Typography>
            {scene.description &&
              scene.description !== "Missing Translation!" && (
                <QrInfoTag color={color} description={scene.description} />
              )}
          </Flex>
        </Flex>
      </Flex>
    </Paper>
  );
};

export default React.memo(QrSceneCard);

const QrIcon = styled("img")`
  width: 5rem;
  aspect-ratio: 1/1;
`;
